import { apiClient } from '@/lib/apiClient';

/** A `##` section of a help page; `id` is `<page id>#<anchor>`. */
export interface HelpSection {
  id: string;
  anchor: string;
  heading: string;
  markdown: string;
}

export interface HelpPage {
  id: string;
  title: string;
  summary: string;
  kind: string;
  module: string;
  /** The language the page is written in: the UI language, or English where it is not translated. */
  language: string;
  sections: HelpSection[];
}

/** A page in the index of all help: everything but its text. */
export type HelpPageSummary = Omit<HelpPage, 'sections'>;

/** A section of a page that matches a search; `id` is `<page id>#<anchor>`. */
export interface HelpSearchSection {
  id: string;
  anchor: string;
  heading: string;
  snippet: string;
}

/**
 * A search result: a page once, with its best-matching sections, or a field's help. For a field,
 * `id` is the field id (`{Entity}.{field}`), `title` the field's label and `entityLabel` its entity's.
 */
export interface HelpSearchHit {
  kind: 'page' | 'field';
  id: string;
  title: string;
  entityLabel: string;
  snippet: string;
  module: string;
  /** The module as the menu names it. */
  moduleLabel: string;
  language: string;
  sections: HelpSearchSection[];
}

/** How many results a module has for the search, whatever module the results are narrowed to. */
export interface HelpSearchModule {
  module: string;
  label: string;
  count: number;
}

export interface HelpSearchResult {
  modules: HelpSearchModule[];
  hits: HelpSearchHit[];
}

/** A field's help text, as the form shows it in the field's tooltip. */
export interface HelpField {
  id: string;
  entity: string;
  entityLabel: string;
  label: string;
  text: string;
  language: string;
}

/**
 * The help endpoint (docs/15-designspecs/User-Help-And-Setup-Guides.md §Surfaces). The backend
 * answers in the request's UI language — apiClient sends it as Accept-Language — and only with
 * pages the user may see.
 */
export const helpApi = {
  context: async (entity: string): Promise<HelpPage[]> =>
    (await apiClient.get<HelpPage[]>('/api/core/help/context', { params: { entity } })).data,

  list: async (): Promise<HelpPageSummary[]> => (await apiClient.get<HelpPageSummary[]>('/api/core/help')).data,

  page: async (pageId: string): Promise<HelpPage> =>
    (await apiClient.get<HelpPage>(`/api/core/help/page/${encodeURIComponent(pageId)}`)).data,

  field: async (fieldId: string): Promise<HelpField> =>
    (await apiClient.get<HelpField>(`/api/core/help/field/${encodeURIComponent(fieldId)}`)).data,

  /** `module` narrows the results to one module; the module counts still cover all of them. */
  search: async (query: string, entity?: string, module?: string): Promise<HelpSearchResult> =>
    (await apiClient.get<HelpSearchResult>('/api/core/help/search', { params: { query, entity, module } })).data,
};
